import {getNarrativeFormResultView} from "./formResultHelpers.js"

function filled(value) {
	return typeof value === "string" && !!value.trim()
}

export function getFormScriptConfigErrors(config = {}) {
	if (!config.scriptConfig) return []
	const fields = Array.isArray(config.fields) ? config.fields : []
	const options = config.scriptConfig.ruleOptions
	const modes = config.scriptConfig.modes
	if (!Array.isArray(options) || !Array.isArray(modes) || !modes.length) return ["Invalid script configuration"]
	const allIds = [...fields, ...options, ...modes].map(item => item.id)
	if (allIds.some(id => !id) || new Set(allIds).size !== allIds.length) return ["Script questions and options need unique IDs"]
	if (options.some(option => !filled(option.title)) || modes.some(mode => !filled(mode.title))) return ["Script options need titles"]
	if (!modes.some(mode => mode.id === config.scriptConfig.modeId)) return ["Select an available script use mode"]
	return []
}

export function getFormScriptView(config = {}, payload = {}) {
	const fields = Array.isArray(config.fields) ? config.fields : []
	const answers = payload.answers || {}
	const options = Array.isArray(config.scriptConfig?.ruleOptions) ? config.scriptConfig.ruleOptions : []
	const selectedIds = Array.isArray(payload.selectedRuleIds) ? payload.selectedRuleIds : []
	const selectedRules = options.filter(option => selectedIds.includes(option.id))
	const removedRules = selectedIds.filter(id => !options.some(option => option.id === id)).map(id => ({id, title: payload.ruleOptionSnapshots?.[id] || "Removed rule"}))
	const target = fields.find(field => field.purpose === "target")
	const script = fields.find(field => field.purpose === "script")
	const prevention = fields.find(field => field.purpose === "prevention")
	const missing = []
	if (target && !filled(answers[target.id])) missing.push(target.title)
	if (script && !filled(answers[script.id])) missing.push(script.title)
	if (prevention && !filled(answers[prevention.id]) && !selectedRules.length) missing.push(prevention.title)
	const mode = (config.scriptConfig?.modes || []).find(item => item.id === config.scriptConfig?.modeId) || null
	const signature = JSON.stringify({target: target && [target.id, target.title, answers[target.id]], script: script && [script.id, script.title, answers[script.id]], prevention: prevention && [prevention.id, prevention.title, answers[prevention.id]], selectedRules: selectedRules.map(item => [item.id, item.title]), mode: mode && [mode.id, mode.title, mode.instructions]})
	const archivedItems = getNarrativeFormResultView(config, {enginePayload: payload}).archivedItems
	return {fields, answers, archivedItems, target, script, prevention, selectedRules, removedRules, mode, missing, canMarkReady: !!target && !!script && !!prevention && !missing.length, isReady: !!payload.readySignature && payload.readySignature === signature, signature}
}

export function updateFormScriptSelection(payload = {}, config = {}, optionId = "", selected = false) {
	const option = (config.scriptConfig?.ruleOptions || []).find(item => item.id === optionId)
	if (!option) throw new Error(`Unknown response-prevention option: ${optionId}`)
	const ids = new Set(Array.isArray(payload.selectedRuleIds) ? payload.selectedRuleIds : [])
	if (selected) ids.add(optionId)
	else ids.delete(optionId)
	return {...payload, selectedRuleIds: [...ids], ruleOptionSnapshots: {...payload.ruleOptionSnapshots, [optionId]: option.title}, readySignature: ""}
}

export function updateFormScriptReady(payload = {}, config = {}, isReady = false) {
	const view = getFormScriptView(config, payload)
	if (isReady && !view.canMarkReady) throw new Error(`Complete ${view.missing.join(", ")} before marking ready`)
	return {...payload, readySignature: isReady ? view.signature : ""}
}

export function createFormScriptSummary(payload = {}, config = {}) {
	const view = getFormScriptView(config, payload)
	const selectedRuleIds = Array.isArray(payload.selectedRuleIds) ? payload.selectedRuleIds : []
	const ruleOptionSnapshots = payload.ruleOptionSnapshots && typeof payload.ruleOptionSnapshots === "object" ? payload.ruleOptionSnapshots : {}
	return {headline: view.target ? String(view.answers[view.target.id] || config.title || "") : String(config.title || ""), form: JSON.parse(JSON.stringify(payload.form || config)), answers: {...view.answers}, selectedRuleIds: [...selectedRuleIds], ruleOptionSnapshots: {...ruleOptionSnapshots}, readiness: view.isReady ? "ready" : "draft", missing: view.missing, scoreResult: null}
}
